import React, { useState, useEffect } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { API_BASE } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, ArrowRight, BookOpen, TrendingUp, Layers, Search, X,
  ChevronDown, ChevronRight, Feather, Landmark, Target, PenLine, Calendar, Compass
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid
} from "recharts";

const CAT_META = {
  "Philosophical": { icon: Feather, grad: "from-violet-500 to-fuchsia-500", soft: "from-violet-50 to-fuchsia-50", border: "border-violet-200", text: "text-violet-700", chip: "bg-violet-100 text-violet-700", bar: "#8b5cf6" },
  "Issue Based": { icon: Landmark, grad: "from-sky-500 to-cyan-500", soft: "from-sky-50 to-cyan-50", border: "border-sky-200", text: "text-sky-700", chip: "bg-sky-100 text-sky-700", bar: "#0ea5e9" },
};
const catMeta = (c) => CAT_META[c] || CAT_META["Philosophical"];

const EssayPYQBank = ({ onPractice, onBack }) => {
  const [view, setView] = useState("overview"); // overview | theme
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [themes, setThemes] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [themeDetail, setThemeDetail] = useState(null);
  const [themeLoading, setThemeLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // pyq_id -> bool
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults(null); setSearching(false); return; }
    setSearching(true);
    const id = setTimeout(async () => {
      try {
        const { data } = await managedAxios.get(`${API_BASE}/essay-pyq/essays?q=${encodeURIComponent(q)}`);
        setResults(data.essays || []);
      } catch (e) {
        if (!isCancelError(e)) console.error(e);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(id);
  }, [query]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [ov, th] = await Promise.all([
          managedAxios.get(`${API_BASE}/essay-pyq/overview`),
          managedAxios.get(`${API_BASE}/essay-pyq/themes`),
        ]);
        if (!alive) return;
        setOverview(ov.data);
        setThemes(th.data.themes || []);
      } catch (e) {
        if (!isCancelError(e)) console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const openTheme = async (slug) => {
    setThemeLoading(true);
    setView("theme");
    setExpanded({});
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      const { data } = await managedAxios.get(`${API_BASE}/essay-pyq/theme/${slug}`);
      setThemeDetail(data);
    } catch (e) {
      if (!isCancelError(e)) console.error(e);
    } finally {
      setThemeLoading(false);
    }
  };

  const shownThemes = catFilter === "all" ? themes : themes.filter((t) => t.category === catFilter);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-400" data-testid="essay-pyq-loading">
        <div className="animate-pulse text-sm">Loading Essay PYQ analysis…</div>
      </div>
    );
  }

  // ─────────────────────────── THEME DETAIL VIEW ───────────────────────────
  if (view === "theme") {
    const t = themeDetail?.theme;
    const essays = themeDetail?.essays || [];
    const meta = catMeta(t?.category);
    const Icon = meta.icon;
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="essay-pyq-theme-view">
        <button
          onClick={() => { setView("overview"); setThemeDetail(null); }}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800"
          data-testid="essay-pyq-theme-back"
        >
          <ArrowLeft className="w-4 h-4" /> All themes
        </button>

        {themeLoading || !t ? (
          <div className="py-16 text-center text-slate-400 animate-pulse">Loading theme…</div>
        ) : (
          <>
            {/* Theme header */}
            <Card className={`border ${meta.border} bg-gradient-to-br ${meta.soft} overflow-hidden`}>
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${meta.grad} flex items-center justify-center shadow-md flex-shrink-0`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={`${meta.chip} border-0 text-xs`}>{t.category}</Badge>
                      <Badge className="bg-white/70 text-slate-700 border-0 text-xs">{t.pyq_count} PYQs</Badge>
                    </div>
                    <h1 className="font-heading text-2xl font-bold text-slate-900 mt-2">{t.theme}</h1>
                    <p className="text-sm text-slate-600 mt-1">{t.core_idea}</p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mt-5">
                  <div className="bg-white/70 rounded-xl p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Central tension</p>
                    <p className="text-sm text-slate-700">{t.central_tension}</p>
                  </div>
                  <div className="bg-white/70 rounded-xl p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">What UPSC is testing</p>
                    <p className="text-sm text-slate-700">{t.what_upsc_is_testing}</p>
                  </div>
                </div>

                {(t.dimensions || []).length > 0 && (
                  <div className="mt-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">Dimensions to develop</p>
                    <div className="flex flex-wrap gap-2">
                      {t.dimensions.map((d, i) => (
                        <span key={i} className={`text-xs px-2.5 py-1 rounded-full ${meta.chip}`}>{d}</span>
                      ))}
                    </div>
                  </div>
                )}

                {(t.years || []).length > 0 && (
                  <p className="text-xs text-slate-500 mt-4">
                    <Calendar className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                    Appeared in: {t.years.join(", ")}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Essays under this theme */}
            <div>
              <h2 className="text-base md:text-lg font-heading font-bold text-slate-800 mb-3">
                Past questions ({essays.length})
              </h2>
              <div className="space-y-3">
                {essays.map((e) => (
                  <EssayCard
                    key={e.pyq_id}
                    e={e}
                    open={!!expanded[e.pyq_id]}
                    onToggle={() => setExpanded((s) => ({ ...s, [e.pyq_id]: !s[e.pyq_id] }))}
                    onPractice={onPractice}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // ─────────────────────────── OVERVIEW VIEW ───────────────────────────
  const trend = overview?.year_breakdown || [];
  const totals = overview?.totals || {};

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="essay-pyq-overview">
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800" data-testid="essay-pyq-back-home">
          <ArrowLeft className="w-4 h-4" /> Essay Suite
        </button>
      )}

      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-rose-500" /> Essay PYQ Analysis
        </h1>
        <p className="text-sm text-slate-500 mt-1">{overview?.total_essays} essays · {overview?.themes_count} themes · {overview?.coverage}</p>
      </div>

      {/* Category weightage cards */}
      <div className="grid grid-cols-2 gap-3">
        {(overview?.categories || []).map((c) => {
          const meta = catMeta(c.category);
          const Icon = meta.icon;
          const pct = totals.total ? Math.round((c.count / totals.total) * 100) : 0;
          return (
            <Card key={c.category} className={`border ${meta.border} bg-gradient-to-br ${meta.soft}`} data-testid={`essay-pyq-cat-${c.category === "Philosophical" ? "phil" : "issue"}`}>
              <CardContent className="p-4">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.grad} flex items-center justify-center shadow-md mb-2`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <p className="font-heading font-bold text-slate-800 text-sm leading-tight">{c.category}</p>
                <p className={`text-4xl font-bold font-heading ${meta.text} mt-1`}>{c.count}</p>
                <p className="text-xs text-slate-500">{pct}% · {c.themes_count} themes</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={query}
          onChange={(ev) => setQuery(ev.target.value)}
          placeholder="Search essay topics by keyword…"
          className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-rose-200"
          data-testid="essay-pyq-search-input"
        />
        {query && (
          <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="essay-pyq-search-clear">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {results !== null ? (
        <div className="space-y-3" data-testid="essay-pyq-search-results">
          <p className="text-xs text-slate-500">{searching ? "Searching…" : `${results.length} result${results.length !== 1 ? "s" : ""} for "${query.trim()}"`}</p>
          {results.map((e) => (
            <EssayCard
              key={e.pyq_id}
              e={e}
              open={!!expanded[e.pyq_id]}
              onToggle={() => setExpanded((s) => ({ ...s, [e.pyq_id]: !s[e.pyq_id] }))}
              onPractice={onPractice}
              context={`${e.theme} · ${e.category}`}
            />
          ))}
          {!searching && results.length === 0 && (
            <p className="text-sm text-slate-400 py-10 text-center">No essays match "{query.trim()}". Try another keyword.</p>
          )}
        </div>
      ) : (
      <>
      {/* Trend chart */}
      <Card className="border border-slate-200 bg-white">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-rose-500" />
            <p className="font-heading font-bold text-slate-800 text-sm">Year-wise trend (Philosophical vs Issue-Based)</p>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={trend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="year" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="philosophical" name="Philosophical" stackId="a" fill="#a855f7" radius={[0, 0, 0, 0]} />
                <Bar dataKey="issue_based" name="Issue-Based" stackId="a" fill="#0ea5e9" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Philosophical essays now dominate (8/8 in 2025, 7/8 in 2026). Prioritise abstract-theme practice.
          </p>
        </CardContent>
      </Card>

      {/* Category filter */}
      <div className="flex items-center gap-2 flex-wrap">
        {["all", "Philosophical", "Issue Based"].map((c) => (
          <button
            key={c}
            onClick={() => setCatFilter(c)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              catFilter === c ? "bg-rose-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid={`essay-pyq-filter-${c === "all" ? "all" : c === "Philosophical" ? "phil" : "issue"}`}
          >
            {c === "all" ? "All themes" : c}
          </button>
        ))}
      </div>

      {/* Theme cards */}
      <div className="grid sm:grid-cols-2 gap-3">
        {shownThemes.map((t) => {
          const meta = catMeta(t.category);
          const Icon = meta.icon;
          return (
            <Card
              key={t.slug}
              style={{ borderLeftColor: meta.bar, borderLeftWidth: "4px" }}
              className={`border ${meta.border} bg-white hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer group`}
              onClick={() => openTheme(t.slug)}
              data-testid={`essay-pyq-theme-card-${t.slug}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${meta.grad} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <Badge className={`${meta.chip} border-0 text-[10px]`}>{t.pyq_count} PYQs</Badge>
                </div>
                <h3 className="font-heading font-bold text-slate-800 text-sm leading-tight group-hover:text-rose-600 transition-colors">{t.theme}</h3>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{t.core_idea}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[10px] text-slate-400">{(t.years || []).length} years</span>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-rose-500 group-hover:translate-x-0.5 transition-all" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      </>
      )}
    </div>
  );
};

const EssayCard = ({ e, open, onToggle, onPractice, context }) => (
  <Card className="border border-slate-200 bg-white overflow-hidden" data-testid={`essay-pyq-essay-${e.pyq_id}`}>
    <button
      className="w-full text-left p-4 flex items-start gap-3 hover:bg-slate-50 transition-colors"
      onClick={onToggle}
      data-testid={`essay-pyq-essay-toggle-${e.pyq_id}`}
    >
      <div className="flex flex-col items-center gap-1 flex-shrink-0">
        <span className="text-xs font-mono font-bold text-rose-500">{e.year}</span>
        {e.section && e.section !== "—" && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Sec {e.section}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        {context && <p className="text-[11px] text-slate-400 mb-1 truncate">{context}</p>}
        <p className={`text-sm font-medium text-slate-800 leading-relaxed ${open ? "" : "line-clamp-2"}`}>{e.essay_topic}</p>
      </div>
      {open ? <ChevronDown className="w-4 h-4 text-slate-400 mt-1" /> : <ChevronRight className="w-4 h-4 text-slate-400 mt-1" />}
    </button>
    {open && (
      <div className="px-4 pb-4 pt-0 space-y-3 border-t border-slate-100">
        {e.question_demand && (
          <Roadmap icon={Target} color="text-rose-600" bg="bg-rose-50" label="Question demand" text={e.question_demand} />
        )}
        {e.student_focus && (
          <Roadmap icon={Compass} color="text-amber-600" bg="bg-amber-50" label="Student focus" text={e.student_focus} />
        )}
        <div className="grid sm:grid-cols-3 gap-2 pt-1">
          <Step n="1" label="Opening" text={e.opening_direction} />
          <Step n="2" label="Body" text={e.body_direction} />
          <Step n="3" label="Conclusion" text={e.conclusion_direction} />
        </div>
        <Button
          className="w-full mt-1 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600"
          onClick={() => onPractice && onPractice(e.essay_topic)}
          data-testid={`essay-pyq-practice-${e.pyq_id}`}
        >
          <PenLine className="w-4 h-4 mr-2" /> Practice this topic
        </Button>
      </div>
    )}
  </Card>
);

const Roadmap = ({ icon: Icon, color, bg, label, text }) => (
  <div className={`${bg} rounded-lg p-3`}>
    <p className={`text-[11px] font-semibold uppercase tracking-wide ${color} mb-1 flex items-center gap-1`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </p>
    <p className="text-sm text-slate-700">{text}</p>
  </div>
);

const Step = ({ n, label, text }) => (
  <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
    <p className="text-[11px] font-bold text-slate-500 mb-1">
      <span className="inline-flex w-4 h-4 rounded-full bg-rose-500 text-white text-[9px] items-center justify-center mr-1">{n}</span>
      {label}
    </p>
    <p className="text-xs text-slate-600 leading-relaxed">{text}</p>
  </div>
);

export default EssayPYQBank;
